<template>
  <div v-if="addressLookupOptionsStore">
    <RichText v-if="bindings.preContent" :class="bindings.preContentClass" :field="{ value: bindings.preContent }" />
    <div class="flex flex-col gap-8 md:gap-6">
      <FormKit
        v-if="!addressLookupOptionsStore.isOverseasSelection"
        ref="addressPicker"
        v-bind="modifiedBindings"
        v-model="displayAddressText"
        :options="lookup"
        type="autocomplete"
        autocomplete="off"
        maxLength="255"
        :data-testid="`${bindings.name}_auto_lookup`"
        :name="bindings.name + '_auto'"
        :innerClass="innerClass || 'input-xl'"
        outerClass=""
        :labelClass="labelClass"
        listboxButtonClass="$reset hidden"
        loaderIconClass="$reset hidden"
        @input="handleSelection"
      />
      <RACQFormKitErrorMessage
        :defaultMessagePlacement="!apiValidationMessage?.text"
        :node="(addressPicker as any)?.node"
        :errorTitle="apiValidationMessage?.title"
        :errorMessage="apiValidationMessage?.text"
      />
      <InformationBox
        v-if="
          addressLookupOptionsStore &&
          !addressLookupOptionsStore.isManualEntries &&
          !addressLookupOptionsStore.riskAddressNotFullyVerifiedIsError &&
          addressLookupOptionsStore.showRiskAddressNotFullyVerifiedMessage
        "
        :description="riskAddressNotFullyVerifiedMessage"
        :type="
          addressLookupOptionsStore.riskAddressNotFullyVerifiedIsError
            ? InformationBoxTypes.ERROR
            : InformationBoxTypes.WARNING
        "
      />

      <RACQFormKit
        v-if="
          addressLookupOptionsStore?.config?.showPostalSameAsInsuredAddressSelection &&
          !addressLookupOptionsStore.isOverseasSelection
        "
        v-model="addressLookupOptionsStore.postalSameAsInsuredAddressSelection"
        :name="`${bindings.name}_sameasinsured`"
        :data-testid="`${bindings.name}_sameasinsured`"
        :bindings="
          {
            label: postalSameAsInsuredAddressLabel,
            name: `${bindings.name}__sameasinsured`,
            outerClass: `my-2`,
            type: 'checkbox',
            placeholder: '',
            classesOptions: '',
          } as VueFormInputProps
        "
        @change="postalSameAsInsuredAddressChanged"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { FormKit } from '@formkit/vue';
import { cloneDeep } from 'lodash-es';
import { AddressLookupService } from '@/services/store';
import type { IDropdownItem, IExperianSuggestion, IExperianResults, IExperianFormatResult } from '@/interfaces';
import { hideAllPoppersAndPaddings } from '@/plugins/tooltip/renderTooltip';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';
import RACQFormKitErrorMessage from '@/components/RACQFormKit/RACQFormKitErrorMessage.vue';
import { VueFormInputProps } from '@/composables/useFormInputData';
import {
  addressLookupEmits,
  emitAddressSelected,
  useFormStore,
  useJeopardyStore,
  useMultiInstanceAddressOptionsStore,
} from '@/store';
import { isPostalAddress } from '@/utils/regex-helpers';
import { RACQAddressResult } from '@/interfaces';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { setFormkitInputValidationMessage } from '@/utils/formkit-helpers';
import { QLD } from '@/constants/general-words.const';

const emit = defineEmits(addressLookupEmits);

const props = defineProps<{
  bindings: VueFormInputProps;
  instanceIdentifier: string;
  innerClass?: string;
  labelClass?: string;
  defaultIsOverseasOption?: boolean;
  showManualEntries?: boolean;
  streetNoRequired: string;
  streetRequired: string;
  suburbRequired: string;
  stateRequired: string;
  postcodeRequired: string;
  countryRequired: string;
  checkAddressRequired: string;
  selectSuburbRequired: string;
  postcodeInvalid: string;
  invalidInsuranceRiskSuburbMessageTitle: string;
  invalidInsuranceRiskSuburbMessageText: string;
  postalSameAsInsuredAddressLabel: string;
  riskAddressNotFullyVerifiedMessage: string;
  riskAddressCycloneCheckFailedMessage: string;
  postalAddressTooltipVariant: string;
  riskAddressTooltipVariant: string;
  physicalAddressTooltipVariant: string;
  throwJpForNonQldAddress?: string;
}>();

const formStore = useFormStore();
const multiInstanceAddressStore = useMultiInstanceAddressOptionsStore();
const addressLookupOptionsStore = multiInstanceAddressStore.getInstance(props.instanceIdentifier);
const jpStore = useJeopardyStore();

const addressPicker = ref(null);
const displayAddressText = ref<string>();

const apiValidationMessage = computed(() => {
  if (addressLookupOptionsStore?.externalValidationMessage) {
    return { title: '', text: addressLookupOptionsStore?.externalValidationMessage };
  } else if (addressLookupOptionsStore.showInvalidInsuranceRiskSuburbMessage) {
    return { title: props.invalidInsuranceRiskSuburbMessageTitle, text: props.invalidInsuranceRiskSuburbMessageText };
  } else if (
    addressLookupOptionsStore.riskAddressNotFullyVerifiedIsError &&
    addressLookupOptionsStore.showRiskAddressNotFullyVerifiedMessage
  ) {
    return { title: '', text: props.riskAddressNotFullyVerifiedMessage };
  } else if (addressLookupOptionsStore.showRiskAddressCycloneCheckFailedMessage) {
    return { title: '', text: props.riskAddressCycloneCheckFailedMessage };
  }
  return null;
});

const modifiedBindings = computed(() => {
  let tempBindings = cloneDeep(props.bindings);

  if (!tempBindings.tooltip) return tempBindings;

  const usePostalAddressTooltip =
    addressLookupOptionsStore.config?.isPostalAddress && props.postalAddressTooltipVariant?.length > 0;
  const useRiskAddressTooltip =
    addressLookupOptionsStore.config?.allowPhysicalAddressesOnly &&
    addressLookupOptionsStore.config?.allowValidInsuranceRiskSuburbsOnly &&
    props.riskAddressTooltipVariant?.length > 0;
  const usePhysicalAddressTooltip =
    (!addressLookupOptionsStore.config?.isPostalAddress ||
      (addressLookupOptionsStore.config?.allowPhysicalAddressesOnly &&
        addressLookupOptionsStore.config?.allowValidInsuranceRiskSuburbsOnly)) &&
    props.riskAddressTooltipVariant?.length > 0;

  if (usePostalAddressTooltip) {
    tempBindings.tooltip.content = props.postalAddressTooltipVariant;
  } else if (useRiskAddressTooltip) {
    tempBindings.tooltip.content = props.riskAddressTooltipVariant;
  } else if (usePhysicalAddressTooltip) {
    tempBindings.tooltip.content = props.physicalAddressTooltipVariant;
  }
  return tempBindings;
});

// To watch changes from other components / steps
watch(
  () => addressLookupOptionsStore?.autoAddressComponents,
  (newValue) => {
    if (addressLookupOptionsStore.isManualEntries && newValue.addressLine != displayAddressText.value) {
      displayAddressText.value = newValue.addressLine;
    } else {
      const newAddress = addressLookupOptionsStore.buildAddressLine(newValue);

      // Do nothing if the component is in manual mode, keep the display text as No - I will enter manually
      if (newAddress != displayAddressText.value) {
        displayAddressText.value = newAddress;
      }
    }
  },
  { immediate: true }
);

watch(
  () => addressLookupOptionsStore?.externalValidationMessage,
  (newValue) => {
    if (newValue !== undefined && addressPicker.value && !addressLookupOptionsStore.isManualEntries) {
      setFormkitInputValidationMessage(addressPicker.value, newValue);
    }
  }
);

watch(displayAddressText, () => {
  if (!addressLookupOptionsStore.isManualEntries) {
    addressLookupOptionsStore.clearAllWarningsAndErrors();
  }
  setFormkitInputValidationMessage(addressPicker.value, '');
});

async function getAddressLookupFormatWithRetry(query: string): Promise<IExperianFormatResult | null> {
  try {
    return await AddressLookupService.getAddressLookupFormat(query);
  } catch (error) {
    console.error('Unable to format address, retrying once: ', error);
  }

  try {
    return await AddressLookupService.getAddressLookupFormat(query);
  } catch (error) {
    console.error('Unable to format address after retry: ', error);
    return null;
  }
}

function postalSameAsInsuredAddressChanged(event: any) {
  event.preventDefault();

  const useAddress = addressLookupOptionsStore.onSameAsPostalAddressChanged();

  //If the postal address has previously been saved as same as insured address and the same as insured address checkbox is unticked
  //we don't want the same address showing so we clear it.
  if (
    !addressLookupOptionsStore.postalSameAsInsuredAddressSelection &&
    addressLookupOptionsStore.checkPostalIsSameAsInsured(addressLookupOptionsStore.postalAddress as RACQAddressResult)
  ) {
    emit(emitAddressSelected, {});
    displayAddressText.value = '';
  } else {
    displayAddressText.value = addressLookupOptionsStore.buildAddressLine(useAddress!);
    emit(emitAddressSelected, useAddress);
  }
}

async function lookup({ search }: { search: string }): Promise<IDropdownItem[]> {
  try {
    if (!search || search.length < 7) return [];

    const data: IExperianResults = await AddressLookupService.getAddressLookup(search);

    if (addressLookupOptionsStore?.config?.allowPhysicalAddressesOnly) {
      data.results = data.results.filter((result) => {
        return !isPostalAddress(result.suggestion);
      });
    }

    data.results.push({
      matched: [],
      suggestion: props.bindings.postContent,
      format: '',
      triggerManualEntry: true,
    });

    return (
      data.results?.map((item: IExperianSuggestion) => ({
        label: item.suggestion,
        value: item,
      })) || []
    );
  } catch (error) {
    console.error('Unable to lookup: ', error);
    return [];
  }
}

async function applyRiskAddressBusinessRules() {
  if (addressLookupOptionsStore?.config?.allowValidInsuranceRiskSuburbsOnly) {
    formStore.showLoadingForCurrentStep();
    await addressLookupOptionsStore.applyRiskAddressBusinessRules();
    setFormkitInputValidationMessage(addressPicker.value, apiValidationMessage.value?.text);

    formStore.showLoadingForCurrentStep(false);
  }
}

function handleNonQldAddresses(state?: string): boolean {
  if (props.throwJpForNonQldAddress != null && state != null && state != QLD) {
    jpStore.showJeopardyPanel({
      jpCode: props.throwJpForNonQldAddress,
      isChatEnabled: true,
      isCallUsVisible: true,
    });

    return true;
  }

  return false;
}

async function handleSelection(address: IExperianSuggestion) {
  // Will be triggered when setting 'Not on list - I will enter manually' hence check for string.
  // If isManualEntries = true and no address exit to prevent resetManualAddress from hiding manual address.
  if (typeof address == 'string' || (!address && addressLookupOptionsStore.isManualEntries)) {
    return;
  }

  if (!address) {
    // hide manual entry fields if user cleared address lookup field
    addressLookupOptionsStore.resetManualAddress();
    return;
  } else if (address.triggerManualEntry) {
    if (addressLookupOptionsStore.isManualEntries) {
      return;
    }

    addressLookupOptionsStore.userHasTriggeredChanged = true;
    addressLookupOptionsStore.onEnableManualEntrySelected();

    setTimeout(() => {
      hideAllPoppersAndPaddings();
    }, 1000);
    return;
  }

  addressLookupOptionsStore.resetManualAddress();

  // Format Address
  if (address.format) {
    const data: IExperianFormatResult | null = await getAddressLookupFormatWithRetry(address.format.split('?')[1]);
    if (!data) {
      emit(emitAddressSelected, {});
      return;
    }

    /*
    Ticket: PJ342-3749
    Example: '1 PO Box 1, Logan City DC QLD 4114'
    Sitecore 8 will end up formatting it like this: 'PO Box 1, LOGAN CITY DC, QLD, 4114'
    
    If the address is a postal address.
    1. Clear street number.
    2. Set address line to be deliveryService1 which should contain 'PO Box 1'.
    */
    const isPostal = isPostalAddress(data.components?.find((comp) => comp.deliveryService1)?.deliveryService1 || '');
    const street =
      data.components?.find((comp) => comp.street1)?.street1 ||
      data.components?.find((comp) => comp.deliveryService1)?.deliveryService1;

    let streetNumber = data.components?.find((comp) => comp.streetNumber1)?.streetNumber1 || '';
    let addressLine = data.address
      .filter((comp) => comp.addressLine1 || comp.addressLine2 || comp.addressLine3)
      .map((item) => Object.values(item).toString())
      .join(' ');

    if (isPostal) {
      streetNumber = '';
      addressLine = data.components?.find((comp) => comp.deliveryService1)?.deliveryService1 || '';
    }

    addressLookupOptionsStore.autoAddressComponents = {
      postalDeliveryType: data.components?.find((comp) => comp.deliveryService1)?.deliveryService1 || '',
      addressLine: addressLine,
      unitNumber: data.components?.find((comp) => comp.subBuilding1)?.subBuilding1 || '',
      buildingLevel: data.components?.find((comp) => comp.subBuilding2)?.subBuilding2 || '',
      buildingName: data.components?.find((comp) => comp.building1)?.building1 || '',
      streetNumber: streetNumber,
      street: street,
      suburb: data.components?.find((comp) => comp.locality1)?.locality1 || '',
      postcode: data.components?.find((comp) => comp.postalCode1)?.postalCode1 || '',
      state: data.address?.find((comp) => comp.province)?.province || '',
      country: data.components?.find((comp) => comp.country1)?.country1 || '',
      countryISO: data.components?.find((comp) => comp.countryISO1)?.countryISO1 || '',
      dpid: data.components?.find((comp) => comp.deliveryPointId1)?.deliveryPointId1 || '',
    };

    // If a non-QLD address is selected and the throwJpForNonQldAddress prop has a value, throw a JP
    // and break out of this function early so no other validation rules/messages are executed
    if (handleNonQldAddresses(addressLookupOptionsStore.autoAddressComponents.state)) return;

    await applyRiskAddressBusinessRules();

    //Clear street before emitting the address.
    if (
      addressLookupOptionsStore.config?.allowPhysicalAddressesOnly &&
      isPostalAddress(addressLookupOptionsStore.autoAddressComponents.street ?? '')
    ) {
      addressLookupOptionsStore.autoAddressComponents.street = '';
    }

    addressLookupOptionsStore.onAutoAddressSelected(addressLookupOptionsStore.autoAddressComponents);

    emit(emitAddressSelected, addressLookupOptionsStore.autoAddressComponents);
  }
}
</script>
